// Drop Item 관련 API 서비스
import { supabase } from '../lib/supabase';

export interface DropItem {
  item_key: string;
  item_name: string;
  drop_from_entity: string;
  equipment_type: 'WEAPON' | 'ARMOR' | 'ACCESSORY';
  equipment_category_key: string;
  item_set_key?: string;
  stat_str?: number;
  stat_dex?: number;
  stat_vit?: number;
  stat_int?: number;
  stat_foc?: number;
  stat_wiz?: number;
  stat_physic_attack_prof?: number;
  stat_magic_spell_prof?: number;
  stat_physic_block_prof?: number;
  stat_physic_dodge_prof?: number;
  stat_magic_resist_prof?: number;
  stat_physic_armor?: number;
  stat_magic_armor?: number;
  stat_fire_armor?: number;
  stat_frost_armor?: number;
  stat_poison_armor?: number;
  stat_shadow_armor?: number;
  stat_electr_armor?: number;
  memo?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DropItemSet {
  set_key: string;
  set_name: string;
  set2_stat_key?: string;
  set2_value?: number;
  set3_stat_key?: string;
  set3_value?: number;
  set4_stat_key?: string;
  set4_value?: number;
  set5_stat_key?: string;
  set5_value?: number;
  created_at?: string;
  updated_at?: string;
}

export interface WeaponCategory {
  id: string;
  key: string;
  name: string;
  description?: string;
  comment?: string;
  slot: string;
  order: string;
  profession?: string;
  is_magic?: boolean;
  is_heal?: boolean;
  is_ranged?: boolean;
}

export interface ArmorCategory {
  id: string;
  key: string;
  name: string;
  description?: string;
  comment?: string;
  mat: string;
  slot: string;
  order: string;
}

export interface AccessoryCategory {
  id: string;
  key: string;
  slot: string;
  order: string;
  name: string;
}

export interface StatInfo {
  key: string;
  name: string;
  description?: string;
}

// Drop Item API
export const dropItemApi = {
  // 모든 드롭 아이템 조회
  getAll: async (): Promise<DropItem[]> => {
    const { data, error } = await supabase
      .from('drop_item')
      .select('*')
      .order('item_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 특정 엔티티의 드롭 아이템 조회
  getByEntity: async (entityCode: string): Promise<DropItem[]> => {
    const { data, error } = await supabase
      .from('drop_item')
      .select('*')
      .eq('drop_from_entity', entityCode)
      .order('item_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 드롭 아이템 생성
  create: async (item: Omit<DropItem, 'created_at' | 'updated_at'>): Promise<DropItem> => {
    const { data, error } = await supabase
      .from('drop_item')
      .insert(item)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 드롭 아이템 수정
  update: async (itemKey: string, updates: Partial<DropItem>): Promise<DropItem> => {
    const { data, error } = await supabase
      .from('drop_item')
      .update(updates)
      .eq('item_key', itemKey)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 드롭 아이템 삭제
  delete: async (itemKey: string): Promise<void> => {
    const { error } = await supabase
      .from('drop_item')
      .delete()
      .eq('item_key', itemKey);
    
    if (error) throw error;
  }
};

// Drop Item Set API
export const dropItemSetApi = {
  // 모든 세트 조회
  getAll: async (): Promise<DropItemSet[]> => {
    const { data, error } = await supabase
      .from('drop_item_set')
      .select('*')
      .order('set_name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 세트 생성
  create: async (set: Omit<DropItemSet, 'created_at' | 'updated_at'>): Promise<DropItemSet> => {
    const { data, error } = await supabase
      .from('drop_item_set')
      .insert(set)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 세트 수정
  update: async (setKey: string, updates: Partial<DropItemSet>): Promise<DropItemSet> => {
    const { data, error } = await supabase
      .from('drop_item_set')
      .update(updates)
      .eq('set_key', setKey)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // 세트 삭제
  delete: async (setKey: string): Promise<void> => {
    const { error } = await supabase
      .from('drop_item_set')
      .delete()
      .eq('set_key', setKey);
    
    if (error) throw error;
  }
};

// Equipment Category API
export const equipmentCategoryApi = {
  // 무기 카테고리 조회
  getWeaponCategories: async (): Promise<WeaponCategory[]> => {
    const { data, error } = await supabase
      .schema('hero')
      .from('w_weaponcat2')
      .select('*')
      .order('order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 방어구 카테고리 조회
  getArmorCategories: async (): Promise<ArmorCategory[]> => {
    const { data, error } = await supabase
      .schema('hero')
      .from('w_armorcat2')
      .select('*')
      .order('order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 악세서리 카테고리 조회
  getAccessoryCategories: async (): Promise<AccessoryCategory[]> => {
    const { data, error } = await supabase
      .schema('hero')
      .from('w_accecat')
      .select('*')
      .order('order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 스탯 정보 조회
  getStats: async (): Promise<StatInfo[]> => {
    const { data, error } = await supabase
      .schema('hero')
      .from('w_stat')
      .select('*')
      .order('key', { ascending: true });
    
    if (error) throw error;
    return data || [];
  }
};